import React, { useEffect, useState } from "react";
import { Button } from "../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../design/dialog";
import { Input } from "../design/input";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
} from "../design/fieldset";
import { Select } from "../design/select";
import { Textarea } from "../design/textarea";
import { PlusIcon } from "@heroicons/react/16/solid";
import { useFinancialItem } from "../contexts/FinancialItemContext";
import { FinancialItem, financialItemTypes } from "../models/FinancialItem";
import { Checkbox, CheckboxField } from "../design/checkbox";

interface Props {
  className?: string;
  children?: React.ReactNode;
  financialItem?: FinancialItem;
  onFinancialItemCreated?: (financialItemId: string) => void;
}

interface Errors {
  name?: string;
  type?: string;
  category?: string;
  amount?: string;
  associatedDebtAmount?: string;
  dateValued?: string;
  dateAcquired?: string;
  ownershipStatus?: string;
  ownershipPercentage?: string;
}

const FinancialItemButton: React.FC<Props> = ({
  className,
  children,
  financialItem,
  onFinancialItemCreated,
}) => {
  const { createFinancialItem, updateFinancialItem } = useFinancialItem();
  const [isOpen, setIsOpen] = useState(false);

  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [type, setType] = useState<FinancialItem["type"] | null>(null);
  const [category, setCategory] = useState<FinancialItem["category"]>("asset");
  const [amount, setAmount] = useState<number>(0);
  const [associatedDebtAmount, setAssociatedDebtAmount] = useState<number>(0);
  const [currency, setCurrency] = useState<string>("USD");
  const [dateValued, setDateValued] = useState<Date | undefined>(undefined);
  const [dateAcquired, setDateAcquired] = useState<Date | undefined>(undefined);
  const [ownershipStatus, setOwnershipStatus] =
    useState<FinancialItem["ownershipStatus"]>("separate");
  const [ownershipPercentage, setOwnershipPercentage] = useState<number>(100);

  const [errors, setErrors] = useState<Errors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isCreatingMore, setIsCreatingMore] = useState(false);

  useEffect(() => {
    if (financialItem && isOpen) {
      setName(financialItem.name ?? "");
      setDescription(financialItem.description ?? "");
      setType(financialItem.type ?? null);
      setCategory(financialItem.category ?? "asset");
      setAmount(financialItem.amount ?? 0);
      setAssociatedDebtAmount(financialItem.associatedDebtAmount ?? 0);
      setCurrency(financialItem.currency ?? "USD");
      setDateValued(
        financialItem.dateValued
          ? new Date(financialItem.dateValued)
          : undefined,
      );
      setDateAcquired(
        financialItem.dateAcquired
          ? new Date(financialItem.dateAcquired)
          : undefined,
      );
      setOwnershipStatus(financialItem.ownershipStatus ?? null);
      setOwnershipPercentage(financialItem.ownershipPercentage ?? 100);
    }
  }, [financialItem, isOpen]);

  const validateForm = () => {
    const newErrors: Errors = {};
    if (!name.trim()) newErrors.name = "A name is required.";
    if (!type) newErrors.type = "A type is required.";
    if (!category) newErrors.category = "A category is required.";
    if (amount <= 0) newErrors.amount = "Amount must be greater than 0.";
    if (associatedDebtAmount < 0)
      newErrors.associatedDebtAmount =
        "Associated debt amount cannot be negative.";
    if (dateValued && dateValued > new Date())
      newErrors.dateValued = "Date valued cannot be in the future.";
    if (dateAcquired && dateAcquired > new Date())
      newErrors.dateAcquired = "Date acquired cannot be in the future.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const clearForm = (blockClose = false) => {
    if (!financialItem) {
      setName("");
      setDescription("");
      setType(null);
      setCategory("asset");
      setAmount(0);
      setAssociatedDebtAmount(0);
      setCurrency("USD");
      setDateValued(undefined);
      setDateAcquired(undefined);
      setOwnershipStatus("separate");
      setOwnershipPercentage(100);
    }
    if (!blockClose) setIsOpen(false);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsSaving(true);

    try {
      if (financialItem) {
        await updateFinancialItem(financialItem._id, {
          name,
          description,
          type: type!,
          category,
          amount,
          associatedDebtAmount,
          currency,
          dateValued,
          dateAcquired,
          ownershipStatus,
          ownershipPercentage,
        });
      } else {
        const newItemId = await createFinancialItem({
          name,
          description,
          type: type!,
          category,
          amount,
          associatedDebtAmount,
          currency,
          dateValued,
          dateAcquired,
          ownershipStatus,
          ownershipPercentage,
        });
        if (onFinancialItemCreated) {
          onFinancialItemCreated(newItemId);
        }
      }
      clearForm(isCreatingMore);
    } catch (error) {
      console.error("Error submitting financial item:", error);
      alert("Failed to submit financial item. Please try again later.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        className={className}
        onClick={() => setIsOpen(true)}
      >
        {children ?? (
          <div className="flex items-center gap-2">
            {!financialItem && <PlusIcon className="w-4 h-4" />}
            <span className="hidden sm:block">
              {financialItem ? "Edit Item" : "Create Item"}
            </span>
          </div>
        )}
      </Button>

      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>
          {financialItem ? "Edit Financial Item" : "Create Financial Item"}
        </DialogTitle>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Name</Label>
                <Input
                  type="text"
                  value={name}
                  placeholder="Name"
                  onChange={(e) => setName(e.target.value)}
                />
                {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
              </Field>

              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
                <Field>
                  <Label>Type</Label>
                  <Select
                    value={type || ""}
                    onChange={(e) =>
                      setType(e.target.value as FinancialItem["type"])
                    }
                  >
                    <option value="" disabled>
                      Select type
                    </option>
                    {financialItemTypes.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.title}
                      </option>
                    ))}
                  </Select>
                  {errors.type && <ErrorMessage>{errors.type}</ErrorMessage>}
                </Field>

                <Field>
                  <Label>Category</Label>
                  <Select
                    value={category || ""}
                    onChange={(e) =>
                      setCategory(e.target.value as FinancialItem["category"])
                    }
                  >
                    <option value="asset">Asset</option>
                    <option value="debt">Debt</option>
                  </Select>
                </Field>
              </div>

              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
                <Field>
                  <Label>Amount</Label>
                  <Input
                    type="number"
                    value={amount || ""}
                    onChange={(e) => setAmount(Number(e.target.value))}
                    placeholder="Amount"
                  />
                </Field>

                <Field>
                  <Label>Associated Debt Amount</Label>
                  <Input
                    type="number"
                    value={associatedDebtAmount || ""}
                    onChange={(e) =>
                      setAssociatedDebtAmount(Number(e.target.value))
                    }
                    placeholder="Associated Debt Amount"
                  />
                </Field>
              </div>

              <Field>
                <Label>Description</Label>
                <Textarea
                  rows={5}
                  value={description}
                  placeholder="Description"
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Field>

              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
                <Field>
                  <Label>Date Valued</Label>
                  <Input
                    type="date"
                    value={
                      dateValued ? dateValued.toISOString().split("T")[0] : ""
                    }
                    onChange={(e) => setDateValued(new Date(e.target.value))}
                  />
                </Field>

                <Field>
                  <Label>Date Acquired</Label>
                  <Input
                    type="date"
                    value={
                      dateAcquired
                        ? dateAcquired.toISOString().split("T")[0]
                        : ""
                    }
                    onChange={(e) => setDateAcquired(new Date(e.target.value))}
                  />
                </Field>
              </div>

              <Field>
                <Label>Ownership Status</Label>
                <Select
                  value={ownershipStatus || ""}
                  onChange={(e) => {
                    const status = e.target
                      .value as FinancialItem["ownershipStatus"];
                    setOwnershipStatus(status);
                    setOwnershipPercentage(status === "marital" ? 50 : 100);
                  }}
                >
                  <option value="">Select Ownership Status</option>
                  <option value="separate">Separate</option>
                  <option value="marital">Marital</option>
                </Select>
              </Field>

              {ownershipStatus === "marital" && (
                <Field>
                  <Label>Ownership Percentage</Label>
                  <Input
                    type="number"
                    value={ownershipPercentage || ""}
                    onChange={(e) =>
                      setOwnershipPercentage(Number(e.target.value))
                    }
                    placeholder="Ownership Percentage"
                  />
                </Field>
              )}
            </FieldGroup>
          </Fieldset>
        </DialogBody>

        <DialogActions className="justify-between">
          {!financialItem && (
            <CheckboxField>
              <Checkbox
                checked={isCreatingMore}
                onChange={(checked) => setIsCreatingMore(checked)}
              />
              <Label>Create more</Label>
            </CheckboxField>
          )}
          <div className="flex gap-2">
            <Button plain onClick={() => clearForm(false)}>
              Cancel
            </Button>
            <Button disabled={isSaving} onClick={handleSubmit}>
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FinancialItemButton;
